import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "utils/api";
import { adminBrandParams } from "utils/brand";
import Calendar, { toISODate } from "components/Calendar";
import { formatPrice } from "constants/massages";
import { useTeamLocale } from "./TeamLocaleContext";
import { useTeamAdminBrand } from "./TeamAdminBrandContext";
import {
  WAKEUP_STUDIO_OPTION,
  CLASSIC_STUDIO_OPTION,
  PREMIUM_STUDIO_OPTION,
  BUSINESS_OPTIONS,
  LEGACY_OPTIONS,
  isDeliveryChoiceOption,
  optionForDelivery,
  availabilityServiceKeyForOption,
  wakeupServiceDurationMinutes,
  isWakeupBookingOption,
  isWakeupMobileOption,
  isWakeupPlusOption,
  usesDailyHoffmannSchedule,
  isThursdayOnlyStudio,
  isLicensePartnerOption,
  isStarterPackageOption,
  skipsTimeslotSelection,
  buildServiceAddressForOption,
  buildLocalPricePreview,
  studioLocationForOption,
  studioScheduleForOption,
  filterSlotsForOption,
  bookingHoursHintForOption,
  slotStepMinutesForOption,
  arrangedAppointmentStartISO,
  licensePartnerMinDate,
  isAfterHoffmannOption1Cutoff,
  formatTime,
  hasAdjustableTravelFee,
  durationChoicesForOption,
  initialDurationMinutesForOption,
} from "scenes/booking";

// Same catalog as /buchen — adding/editing an option there flows through here.
// Delivery-choice services (Studio vs. Vor Ort / Edeka) appear once; the location
// is picked via a second dropdown that only shows for those services.
const OPTION_ENTRIES = [
  { key: "wakeup", option: WAKEUP_STUDIO_OPTION },
  { key: "classic", option: CLASSIC_STUDIO_OPTION },
  { key: "premium", option: PREMIUM_STUDIO_OPTION },
  ...BUSINESS_OPTIONS.map((o) => ({ key: o.id, option: o })),
  ...LEGACY_OPTIONS.map((o) => ({ key: o.id, option: o })),
].filter((entry) => entry.option);

const EDEKA_LOCATION = "Edeka, Ölmühlenweg 4";
const MANUAL_OPEN_HOUR = 8;
const MANUAL_CLOSE_HOUR = 22;
const MANUAL_HOUR_CHOICES = Array.from(
  { length: MANUAL_CLOSE_HOUR - MANUAL_OPEN_HOUR },
  (_, idx) => MANUAL_OPEN_HOUR + idx
);
const MANUAL_MINUTE_CHOICES = Array.from({ length: 60 }, (_, idx) => idx);

const labelForEntry = (entry) => entry.option.title;

const isFreeConsultationOption = (option) =>
  option?.bookingMode === "consultation" && !option?.paidPackage;

const manualHoursHintForOption = (option, date) => {
  const hint = bookingHoursHintForOption(option, date);
  if (!hint) {
    return "Termine sind ab 08:00 Uhr buchbar (manuelle Buchung).";
  }
  return String(hint).replace(/10:00/g, "08:00");
};

const buildManualStartISO = (date, hour, minute) => {
  if (!date || hour === "" || minute === "" || hour == null || minute == null) {
    return null;
  }
  const ymd = toISODate(date);
  const hh = String(hour).padStart(2, "0");
  const mm = String(minute).padStart(2, "0");
  const parsed = new Date(`${ymd}T${hh}:${mm}:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
};

const ManualBookingSuccessDialog = ({ open, details, onClose, t }) => {
  const closeButtonRef = useRef(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setCopied(false);
    closeButtonRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const statusHint =
    details?.status === "reserved-awaiting-payment"
      ? t("manual.successStatusReserved")
      : details?.status === "awaiting-immediate-payment"
        ? t("manual.successStatusImmediate")
        : null;

  const copyPaymentLink = async () => {
    if (!details?.paymentUrl) return;
    try {
      await navigator.clipboard.writeText(details.paymentUrl);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return createPortal(
    <div className="team-manual-success-root">
      <div
        className="team-manual-success-overlay"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="team-manual-success-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="team-manual-success-title"
        aria-describedby="team-manual-success-desc"
      >
        <span className="team-manual-success-mark" aria-hidden="true" />
        <p className="team-manual-success-kicker">{t("manual.successKicker")}</p>
        <h2 id="team-manual-success-title">{t("manual.successTitle")}</h2>
        <p id="team-manual-success-desc" className="team-manual-success-lead">
          {t(details?.successLeadKey || "manual.successLead", { email: details?.email })}
        </p>
        <dl className="team-manual-success-details">
          <div>
            <dt>{t("manual.successService")}</dt>
            <dd>{details?.service || t("calendar.bookingDetail.none")}</dd>
          </div>
          <div>
            <dt>{t("manual.successNotification")}</dt>
            <dd>
              {details?.notifyCustomer
                ? t("manual.successNotificationYes")
                : t("manual.successNotificationNo")}
            </dd>
          </div>
          {statusHint && (
            <div>
              <dt>{t("bookings.col.status")}</dt>
              <dd>{statusHint}</dd>
            </div>
          )}
          {details?.paymentUrl && (
            <div>
              <dt>{t("manual.successPaymentLink")}</dt>
              <dd>
                <a
                  href={details.paymentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ wordBreak: "break-all" }}
                >
                  {details.paymentUrl}
                </a>
                <div style={{ marginTop: "0.5rem" }}>
                  <button
                    type="button"
                    className="auth-submit"
                    onClick={copyPaymentLink}
                    style={{ width: "auto", padding: "0.4rem 0.9rem" }}
                  >
                    {copied
                      ? t("manual.successPaymentLinkCopied")
                      : t("manual.successPaymentLinkCopy") || "Link kopieren"}
                  </button>
                </div>
              </dd>
            </div>
          )}
        </dl>
        <button
          type="button"
          className="auth-submit team-manual-success-close"
          onClick={onClose}
          ref={closeButtonRef}
        >
          {t("manual.successClose")}
        </button>
      </div>
    </div>,
    document.body
  );
};

const ManualBookingForm = ({
  onCreated,
  createEndpoint = "/admin/bookings",
  conflictEndpoint = "/admin/bookings",
  conflictParams = null,
  submitLabelKey = "manual.submit",
  formLeadKey = "manual.formLead",
  successLeadKey = "manual.successLead",
}) => {
  const { t } = useTeamLocale();
  const adminBrand = useTeamAdminBrand();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [optionKey, setOptionKey] = useState("");
  const [delivery, setDelivery] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [customHour, setCustomHour] = useState("");
  const [customMinute, setCustomMinute] = useState("");
  const [conflictWarning, setConflictWarning] = useState(false);
  const [personCount, setPersonCount] = useState(1);
  const [durationMinutes, setDurationMinutes] = useState("");
  const [address, setAddress] = useState({ companyName: "", fullAddress: "", notes: "" });
  const [hasVoucher, setHasVoucher] = useState(false);
  const [voucherTermsAccepted, setVoucherTermsAccepted] = useState(false);
  const [applyTravelFee, setApplyTravelFee] = useState(true);
  const [useCustomPrice, setUseCustomPrice] = useState(false);
  const [customPriceEuro, setCustomPriceEuro] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [notes, setNotes] = useState("");
  const [notifyCustomer, setNotifyCustomer] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const baseEntry = OPTION_ENTRIES.find((e) => e.key === optionKey) || null;
  const baseOption = baseEntry?.option || null;
  const isDeliveryChoice = isDeliveryChoiceOption(baseOption);
  const deliveryResolved =
    delivery === "studio" || delivery === "onsite" || delivery === "edeka";
  const comesToYou = delivery === "onsite";
  const option =
    !baseOption
      ? null
      : isDeliveryChoice
        ? deliveryResolved
          ? optionForDelivery(baseOption, comesToYou)
          : null
        : baseOption;

  const appointmentIsArranged = skipsTimeslotSelection(option);
  const requiresAddress = Boolean(option?.requiresAddress);
  const requiresVoucherQuestion = Boolean(option?.requiresVoucherQuestion);
  const isFreeConsultation = isFreeConsultationOption(option);
  const showTravelFeeToggle = hasAdjustableTravelFee(option) && !hasVoucher;

  const personCountChoices = useMemo(() => {
    if (!option?.personCountMax) return [];
    const min = Number(option.personCountMin || 1);
    const max = Number(option.personCountMax || min);
    return Array.from({ length: Math.max(0, max - min + 1) }, (_, idx) => min + idx);
  }, [option]);
  const usesPersonCountInput = isWakeupPlusOption(option);
  const personCountMin = Number(option?.personCountMin || 1);
  const personCountMax = Number.isFinite(Number(option?.personCountMax))
    ? Number(option.personCountMax)
    : null;
  const showPersonCount =
    usesPersonCountInput ||
    personCountChoices.length > 0 ||
    Boolean(option?.personCountDefault);
  const selectedPersonCount = Math.max(
    personCountMin,
    Math.min(
      personCountMax ?? Infinity,
      Math.round(Number(personCount || option?.personCountDefault || 1))
    )
  );
  const personCountLabel = isWakeupPlusOption(option) ? "Mitarbeitende" : "Personen";
  const durationChoices = useMemo(() => durationChoicesForOption(option), [option]);
  const showDurationChoice = durationChoices.length > 0;
  const selectedDurationMinutes =
    Number(durationMinutes) > 0 ? Number(durationMinutes) : null;
  const durationChoiceMissing = showDurationChoice && !selectedDurationMinutes;

  const minDate = isLicensePartnerOption(option) ? licensePartnerMinDate() : new Date();
  const maxDate = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);

  const pricePreview = useMemo(() => {
    if (!option || durationChoiceMissing) return null;
    return buildLocalPricePreview(
      option,
      selectedPersonCount,
      hasVoucher,
      applyTravelFee,
      "stripe",
      null,
      selectedDurationMinutes ?? option.durationMinutes
    );
  }, [
    option,
    selectedPersonCount,
    hasVoucher,
    applyTravelFee,
    selectedDurationMinutes,
    durationChoiceMissing,
  ]);
  const displayPriceCents = isFreeConsultation
    ? 0
    : pricePreview?.grossCents ?? (option?.priceCents ?? null);

  const parseEuroToCents = (raw) => {
    const value = String(raw || "").trim().replace(",", ".");
    if (!value) return null;
    const euros = Number(value);
    if (!Number.isFinite(euros) || euros < 0) return null;
    return Math.round(euros * 100);
  };

  const customPriceCents = useCustomPrice ? parseEuroToCents(customPriceEuro) : null;
  const effectivePriceCents =
    useCustomPrice && customPriceCents != null ? customPriceCents : displayPriceCents;

  const updatePersonCount = (next) => {
    const parsed = Math.round(Number(next));
    const fallback = Number(option?.personCountDefault || 1);
    const upper = personCountMax ?? Infinity;
    const bounded = Math.min(
      upper,
      Math.max(personCountMin, Number.isFinite(parsed) ? parsed : fallback)
    );
    setPersonCount(bounded);
  };

  const resetBookingFields = useCallback(() => {
    setSelectedDate(null);
    setSlots([]);
    setSelectedSlot(null);
    setCustomHour("");
    setCustomMinute("");
    setConflictWarning(false);
    setPersonCount(1);
    setDurationMinutes("");
    setAddress({ companyName: "", fullAddress: "", notes: "" });
    setHasVoucher(false);
    setVoucherTermsAccepted(false);
    setApplyTravelFee(true);
    setUseCustomPrice(false);
    setCustomPriceEuro("");
    setNotes("");
  }, []);

  const handleOptionChange = (key) => {
    setOptionKey(key);
    setDelivery("");
    const nextOption = OPTION_ENTRIES.find((e) => e.key === key)?.option || null;
    setSelectedDate(null);
    setSlots([]);
    setSelectedSlot(null);
    setCustomHour("");
    setCustomMinute("");
    setConflictWarning(false);
    setPersonCount(Number(nextOption?.personCountDefault || 1));
    setDurationMinutes(initialDurationMinutesForOption(nextOption));
    setAddress({ companyName: "", fullAddress: "", notes: "" });
    setHasVoucher(false);
    setVoucherTermsAccepted(false);
    setApplyTravelFee(true);
    setUseCustomPrice(false);
    setCustomPriceEuro("");
    setPaymentMethod(isFreeConsultationOption(nextOption) ? "later" : "stripe");
    setError(null);
  };

  const handleDeliveryChange = (nextDelivery) => {
    setDelivery(nextDelivery);
    setSelectedSlot(null);
    setCustomHour("");
    setCustomMinute("");
    setConflictWarning(false);
    setHasVoucher(false);
    setVoucherTermsAccepted(false);
    setApplyTravelFee(true);
    setUseCustomPrice(false);
    setCustomPriceEuro("");
    setAddress({ companyName: "", fullAddress: "", notes: "" });
  };

  const handleCustomPriceToggle = (checked) => {
    setUseCustomPrice(checked);
    if (checked && displayPriceCents != null) {
      setCustomPriceEuro(String((displayPriceCents / 100).toFixed(2)).replace(".", ","));
    } else {
      setCustomPriceEuro("");
    }
  };

  const customStartISO = useMemo(
    () => buildManualStartISO(selectedDate, customHour, customMinute),
    [selectedDate, customHour, customMinute]
  );
  const customTimePartial = customHour !== "" || customMinute !== "";
  const hasCustomTime = customHour !== "" && customMinute !== "";
  const effectiveStartISO = hasCustomTime ? customStartISO : selectedSlot;

  useEffect(() => {
    if (!option || appointmentIsArranged || !selectedDate) {
      setSlots([]);
      setSelectedSlot(null);
      return undefined;
    }
    if (showDurationChoice && !selectedDurationMinutes) {
      setSlots([]);
      setSelectedSlot(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingSlots(true);
    setSelectedSlot(null);
    const availabilityDuration = isWakeupBookingOption(option)
      ? wakeupServiceDurationMinutes(option, selectedPersonCount)
      : selectedDurationMinutes || option.durationMinutes;
    const availabilityPersonCount = isWakeupPlusOption(option)
      ? Math.min(selectedPersonCount, 10)
      : selectedPersonCount;
    const params = {
      date: toISODate(selectedDate),
      durationMinutes: availabilityDuration,
      brand: "dr-hoffmann",
      serviceKey: availabilityServiceKeyForOption(option),
      minimumLeadMinutes: 0,
      ignoreBuffers: true,
      manualAdminHours: true,
    };
    const slotStepMinutes = slotStepMinutesForOption(option);
    if (slotStepMinutes) params.slotStepMinutes = slotStepMinutes;
    if (isWakeupBookingOption(option)) params.personCount = availabilityPersonCount;
    if (option.hoffmannBookingOption) {
      params.hoffmannBookingOption = option.hoffmannBookingOption;
    }
    if (option.bookingMode === "studio-weekday" && !usesDailyHoffmannSchedule(option)) {
      params.studioSchedule = "weekday";
      params.location =
        delivery === "edeka"
          ? EDEKA_LOCATION
          : studioLocationForOption(option, selectedDate);
    } else if (option.bookingMode === "studio-weekday") {
      params.studioSchedule = studioScheduleForOption(option, selectedDate);
      params.location =
        delivery === "edeka"
          ? EDEKA_LOCATION
          : studioLocationForOption(option, selectedDate);
    }
    api
      .get("/bookings/availability", { params })
      .then(({ data }) => {
        if (!cancelled) {
          setSlots(filterSlotsForOption(option, selectedDate, data?.slots));
        }
      })
      .catch(() => {
        if (!cancelled) setSlots([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    optionKey,
    delivery,
    appointmentIsArranged,
    selectedDate,
    selectedPersonCount,
    selectedDurationMinutes,
    showDurationChoice,
  ]);

  useEffect(() => {
    if (!hasCustomTime || !customStartISO || !option || appointmentIsArranged) {
      setConflictWarning(false);
      return undefined;
    }
    const durationMs =
      (isWakeupBookingOption(option)
        ? wakeupServiceDurationMinutes(option, selectedPersonCount)
        : selectedDurationMinutes || option.durationMinutes || 0) *
      60 *
      1000;
    if (!durationMs) {
      setConflictWarning(false);
      return undefined;
    }
    const startMs = new Date(customStartISO).getTime();
    const endMs = startMs + durationMs;
    const day = toISODate(selectedDate);
    let cancelled = false;
    const baseConflictParams =
      typeof conflictParams === "function"
        ? conflictParams({ day, adminBrand })
        : {
            ...adminBrandParams({ brand: adminBrand }),
            from: day,
            to: day,
            limit: 100,
            includeCancelled: undefined,
          };
    api
      .get(conflictEndpoint, {
        params: baseConflictParams,
      })
      .then(({ data }) => {
        if (cancelled) return;
        const overlaps = (data?.bookings || []).some((booking) => {
          const existingStart = new Date(booking.start).getTime();
          const existingEnd = new Date(booking.end).getTime();
          return (
            Number.isFinite(existingStart) &&
            Number.isFinite(existingEnd) &&
            existingStart < endMs &&
            existingEnd > startMs
          );
        });
        setConflictWarning(overlaps);
      })
      .catch(() => {
        if (!cancelled) setConflictWarning(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    hasCustomTime,
    customStartISO,
    option,
    appointmentIsArranged,
    selectedDate,
    selectedPersonCount,
    selectedDurationMinutes,
    adminBrand,
    conflictEndpoint,
    conflictParams,
  ]);

  const updateAddress = (key) => (e) =>
    setAddress((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      return setError("Bitte Vorname, Nachname und E-Mail des Kunden angeben.");
    }
    if (!optionKey) return setError("Bitte eine Leistung auswählen.");
    if (isDeliveryChoice && !deliveryResolved) {
      return setError(t("manual.deliveryRequired"));
    }
    if (!option) return setError("Bitte eine Leistung auswählen.");
    if (durationChoiceMissing) {
      return setError("Bitte eine Dauer auswählen.");
    }
    if (!appointmentIsArranged) {
      if (customTimePartial && !hasCustomTime) {
        return setError(t("manual.customTimeInvalid"));
      }
      if (hasCustomTime) {
        if (!customStartISO) {
          return setError(t("manual.customTimeInvalid"));
        }
        const hourNum = Number(customHour);
        if (hourNum < MANUAL_OPEN_HOUR || hourNum >= MANUAL_CLOSE_HOUR) {
          return setError(t("manual.customTimeInvalid"));
        }
      } else if (!selectedSlot) {
        return setError("Bitte einen Termin auswählen.");
      }
    }
    if (requiresAddress && !address.fullAddress.trim()) {
      return setError("Bitte die Massageadresse angeben.");
    }
    if (requiresVoucherQuestion && hasVoucher && !voucherTermsAccepted) {
      return setError("Bitte bestätigen, dass der Gutschein zum Termin mitgebracht wird.");
    }
    if (useCustomPrice) {
      const cents = parseEuroToCents(customPriceEuro);
      if (cents == null) {
        return setError(t("manual.customPriceInvalid"));
      }
      if (cents > 100_000) {
        return setError(t("manual.customPriceTooHigh"));
      }
    }

    setSubmitting(true);
    try {
      const isWakeupPlus = isWakeupPlusOption(option);
      const bookingPersonCount = isWakeupPlus
        ? Math.min(selectedPersonCount, 10)
        : selectedPersonCount;
      const manualNotes = [
        isWakeupPlus
          ? `Tatsaechliche Mitarbeitendenzahl: ${selectedPersonCount}`
          : "",
        notes.trim(),
      ]
        .filter(Boolean)
        .join("\n");
      const body = {
        customer: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: phone.trim(),
        },
        serviceKey: option.serviceKey,
        durationMinutes: selectedDurationMinutes || option.durationMinutes,
        minimumLeadMinutes: 0,
        paymentMethod: isFreeConsultation ? "later" : paymentMethod,
        notifyCustomer,
        manualAdminHours: true,
      };
      if (option.offerType) body.offerType = option.offerType;
      if (showPersonCount || option.personCountDefault) {
        body.personCount = bookingPersonCount;
      }
      if (isWakeupPlus) {
        body.actualPersonCount = selectedPersonCount;
        body.employeeCount = selectedPersonCount;
        body.manualPersonCount = selectedPersonCount;
        body.priceCents = effectivePriceCents;
      }
      if (isWakeupMobileOption(option)) {
        body.serviceDurationMinutes = wakeupServiceDurationMinutes(
          option,
          selectedPersonCount
        );
      }
      if (requiresVoucherQuestion) {
        body.hasVoucher = hasVoucher;
        body.voucherTermsAccepted = hasVoucher && voucherTermsAccepted;
      }
      if (showTravelFeeToggle) {
        body.applyTravelFee = applyTravelFee;
      }
      if (effectiveStartISO) {
        body.startISO = effectiveStartISO;
      } else if (appointmentIsArranged) {
        body.startISO = arrangedAppointmentStartISO();
      }
      if (isStarterPackageOption(option)) {
        body.hoffmannBookingOption = option.hoffmannBookingOption;
        if (option.priceCents && !useCustomPrice) body.priceCents = option.priceCents;
      } else if (option.hoffmannBookingOption) {
        body.hoffmannBookingOption = option.hoffmannBookingOption;
      } else {
        body.hoffmannBookingOption = option.title;
      }
      if (option.bookingMode === "studio-weekday") {
        const studioSchedule = studioScheduleForOption(option, selectedDate);
        body.location =
          delivery === "edeka"
            ? EDEKA_LOCATION
            : studioLocationForOption(option, selectedDate);
        body.studioSchedule = studioSchedule;
        body.studioScheduleWeekday =
          !usesDailyHoffmannSchedule(option) ||
          studioSchedule === "thursday";
      } else if (delivery === "edeka") {
        body.location = EDEKA_LOCATION;
      }
      if (requiresAddress) {
        body.serviceAddress = buildServiceAddressForOption(option, {
          companyName: address.companyName,
          fullAddress: address.fullAddress,
          notes: address.notes,
        });
        if (address.fullAddress) body.address = address.fullAddress;
      }
      if (manualNotes) body.notes = manualNotes;
      if (useCustomPrice) {
        body.useCustomPrice = true;
        body.priceCents = customPriceCents;
        body.customPriceCents = customPriceCents;
        body.manualPriceOverride = true;
        body.amountEuro = customPriceEuro.trim();
      }

      const { data } = await api.post(createEndpoint, body);
      setSuccess({
        email: data?.booking?.customer?.email || email.trim(),
        service: option.title,
        notifyCustomer,
        paymentUrl: data?.paymentUrl || null,
        status: data?.booking?.status || null,
        successLeadKey,
      });
      if (data?.conflictWarning) {
        setConflictWarning(true);
      }
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setOptionKey("");
      setDelivery("");
      setApplyTravelFee(true);
      setUseCustomPrice(false);
      setCustomPriceEuro("");
      setPaymentMethod("stripe");
      setNotifyCustomer(true);
      resetBookingFields();
      onCreated?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Die Buchung konnte nicht angelegt werden."
      );
    } finally {
      setSubmitting(false);
    }
    return undefined;
  };

  return (
    <form className="team-manual-form" onSubmit={handleSubmit}>
      <ManualBookingSuccessDialog
        open={Boolean(success)}
        details={success}
        onClose={() => setSuccess(null)}
        t={t}
      />
      <p className="team-lead">{t(formLeadKey)}</p>

      {error && <div className="auth-alert is-error">{error}</div>}

      <div className="booking-step">
        <div className="step-head">
          <h2>
            {t("manual.customerTitle")}
          </h2>
          <span className="booking-step-number">01</span>
        </div>
        <div className="address-grid">
          <div>
            <label htmlFor="mb-first">{t("manual.firstName")} *</label>
            <input
              id="mb-first"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="mb-last">{t("manual.lastName")} *</label>
            <input
              id="mb-last"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="mb-email">{t("manual.email")} *</label>
            <input
              id="mb-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="mb-phone">{t("manual.phone")}</label>
            <input
              id="mb-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+49 ..."
            />
          </div>
        </div>
      </div>

      <div className="booking-step">
        <div className="step-head">
          <h2>{t("manual.serviceTitle")}</h2>
          <span className="booking-step-number">02</span>
        </div>
        <div className="address-grid">
          <div className="full">
            <label htmlFor="mb-service">{t("manual.service")} *</label>
            <select
              id="mb-service"
              value={optionKey}
              onChange={(e) => handleOptionChange(e.target.value)}
            >
              <option value="">{t("manual.servicePlaceholder")}</option>
              {OPTION_ENTRIES.map((e) => (
                <option key={e.key} value={e.key}>
                  {labelForEntry(e)}
                </option>
              ))}
            </select>
          </div>
          {isDeliveryChoice && (
            <div className="full">
              <label htmlFor="mb-delivery">{t("manual.deliveryLabel")} *</label>
              <select
                id="mb-delivery"
                value={delivery}
                onChange={(e) => handleDeliveryChange(e.target.value)}
              >
                <option value="">{t("manual.deliveryPlaceholder")}</option>
                <option value="studio">{t("manual.deliveryStudio")}</option>
                <option value="edeka">{t("manual.deliveryEdeka")}</option>
                <option value="onsite">{t("manual.deliveryOnsite")}</option>
              </select>
            </div>
          )}
          {showDurationChoice && (
            <div className="full">
              <label htmlFor="mb-duration">{t("manual.duration")} *</label>
              <select
                id="mb-duration"
                value={selectedDurationMinutes ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setDurationMinutes(value ? Number(value) : "");
                  setSelectedSlot(null);
                  setUseCustomPrice(false);
                  setCustomPriceEuro("");
                }}
              >
                <option value="">{t("manual.durationPlaceholder")}</option>
                {durationChoices.map((minutes) => (
                  <option key={minutes} value={minutes}>
                    {minutes} {t("common.minutes")}
                  </option>
                ))}
              </select>
            </div>
          )}
          {showTravelFeeToggle && (
            <div className="full">
              <label className="team-checkbox">
                <input
                  type="checkbox"
                  checked={applyTravelFee}
                  onChange={(e) => setApplyTravelFee(e.target.checked)}
                />
                {t("manual.applyTravelFee")}
              </label>
            </div>
          )}
          {option && !isFreeConsultation && displayPriceCents != null && (
            <div className="full">
              <label className="team-checkbox">
                <input
                  type="checkbox"
                  checked={useCustomPrice}
                  onChange={(e) => handleCustomPriceToggle(e.target.checked)}
                />
                {t("manual.useCustomPrice")}
              </label>
            </div>
          )}
          {option && useCustomPrice && (
            <div className="full">
              <label htmlFor="mb-custom-price">{t("manual.customPrice")} *</label>
              <input
                id="mb-custom-price"
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                value={customPriceEuro}
                onChange={(e) => setCustomPriceEuro(e.target.value)}
                required
              />
            </div>
          )}
          {option && effectivePriceCents != null && (
            <div className="full">
              <p className="field-note">
                {t("manual.priceLabel")}:{" "}
                <strong>
                  {effectivePriceCents === 0
                    ? t("manual.free")
                    : formatPrice(effectivePriceCents)}
                </strong>
                {useCustomPrice && (
                  <span className="team-muted"> ({t("manual.customPriceActive")})</span>
                )}
              </p>
            </div>
          )}
          {showPersonCount && (
            <div className="full">
              <label htmlFor="mb-person">{personCountLabel} *</label>
              {usesPersonCountInput ? (
                <>
                  <input
                    id="mb-person"
                    type="number"
                    inputMode="numeric"
                    min={personCountMin}
                    step="1"
                    value={selectedPersonCount}
                    onChange={(e) => updatePersonCount(e.target.value)}
                  />
                  <p className="field-note">
                    1-10 Mitarbeitende: 79 € netto zzgl. MwSt. Jede weitere Person: +5 € netto.
                  </p>
                </>
              ) : (
                <select
                  id="mb-person"
                  value={selectedPersonCount}
                  onChange={(e) => updatePersonCount(e.target.value)}
                >
                  {personCountChoices.map((count) => (
                    <option key={count} value={count}>
                      {count}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>
      </div>

      {option && (
        <div className="booking-step">
          <div className="step-head">
            <h2>{t("manual.dateTitle")}</h2>
            <span className="booking-step-number">03</span>
          </div>
          {appointmentIsArranged ? (
            <p className="step-summary">{t("manual.arranged")}</p>
          ) : (
            <div className="team-manual-scheduler">
              <Calendar
                selectedDate={selectedDate}
                onSelect={setSelectedDate}
                minDate={minDate}
                maxDate={maxDate}
                weekdaysOnly={
                  !isThursdayOnlyStudio(option) && !usesDailyHoffmannSchedule(option)
                }
                onlyWeekday={isThursdayOnlyStudio(option) ? 4 : -1}
                closedWeekday={-1}
                disableDate={
                  isThursdayOnlyStudio(option) ? isAfterHoffmannOption1Cutoff : undefined
                }
              />
              {selectedDate && (
                <>
                  {manualHoursHintForOption(option, selectedDate) && (
                    <p className="step-summary" style={{ marginTop: "1rem" }}>
                      {manualHoursHintForOption(option, selectedDate)}
                    </p>
                  )}
                  {loadingSlots ? (
                    <p className="step-summary" style={{ marginTop: "1rem" }}>
                      {t("manual.slotsLoading")}
                    </p>
                  ) : slots.length === 0 ? (
                    <p className="step-summary" style={{ marginTop: "1rem" }}>
                      {durationChoiceMissing
                        ? t("manual.slotsNeedDuration")
                        : t("manual.slotsEmpty")}
                    </p>
                  ) : (
                    <div className="slot-grid">
                      {slots.map((iso) => (
                        <button
                          type="button"
                          key={iso}
                          onClick={() => {
                            setSelectedSlot(iso);
                            setCustomHour("");
                            setCustomMinute("");
                            setConflictWarning(false);
                          }}
                          className={`slot-button ${
                            !hasCustomTime && selectedSlot === iso ? "is-selected" : ""
                          }`}
                        >
                          {formatTime(iso)}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="team-manual-custom-time">
                    <p className="step-summary" style={{ marginTop: "1.25rem" }}>
                      {t("manual.customTimeTitle")}
                    </p>
                    <p className="field-note">{t("manual.customTimeHint")}</p>
                    <div className="team-manual-custom-time-row">
                      <div>
                        <label htmlFor="mb-custom-hour">{t("manual.customTimeHour")}</label>
                        <select
                          id="mb-custom-hour"
                          value={customHour}
                          onChange={(e) => {
                            setCustomHour(e.target.value === "" ? "" : Number(e.target.value));
                            setSelectedSlot(null);
                          }}
                        >
                          <option value="">–</option>
                          {MANUAL_HOUR_CHOICES.map((hour) => (
                            <option key={hour} value={hour}>
                              {String(hour).padStart(2, "0")}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="mb-custom-minute">
                          {t("manual.customTimeMinute")}
                        </label>
                        <select
                          id="mb-custom-minute"
                          value={customMinute}
                          onChange={(e) => {
                            setCustomMinute(
                              e.target.value === "" ? "" : Number(e.target.value)
                            );
                            setSelectedSlot(null);
                          }}
                        >
                          <option value="">–</option>
                          {MANUAL_MINUTE_CHOICES.map((minute) => (
                            <option key={minute} value={minute}>
                              {String(minute).padStart(2, "0")}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {conflictWarning && (
                      <div className="auth-alert is-warning" style={{ marginTop: "0.75rem" }}>
                        {t("manual.conflictWarning")}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {option && requiresAddress && (
        <div className="booking-step">
          <div className="step-head">
            <h2>{t("manual.addressTitle")}</h2>
            <span className="booking-step-number">04</span>
          </div>
          <div className="address-grid">
            <div className="full">
              <label htmlFor="mb-company">{t("manual.companyName")}</label>
              <input
                id="mb-company"
                value={address.companyName}
                onChange={updateAddress("companyName")}
              />
            </div>
            <div className="full">
              <label htmlFor="mb-address">{t("manual.address")} *</label>
              <input
                id="mb-address"
                value={address.fullAddress}
                onChange={updateAddress("fullAddress")}
                placeholder="Straße Hausnummer, PLZ Ort"
              />
            </div>
            <div className="full">
              <label htmlFor="mb-address-notes">{t("manual.addressNotes")}</label>
              <textarea
                id="mb-address-notes"
                rows={2}
                maxLength={300}
                value={address.notes}
                onChange={updateAddress("notes")}
                placeholder="Stockwerk, Klingelschild …"
              />
            </div>
          </div>
        </div>
      )}

      {option && requiresVoucherQuestion && (
        <div className="booking-step">
          <div className="step-head">
            <h2>{t("manual.voucherTitle")}</h2>
            <span className="booking-step-number">05</span>
          </div>
          <label className="team-checkbox">
            <input
              type="checkbox"
              checked={hasVoucher}
              onChange={(e) => {
                setHasVoucher(e.target.checked);
                setVoucherTermsAccepted(false);
                if (e.target.checked) setApplyTravelFee(true);
              }}
            />
            {t("manual.hasVoucher")}
          </label>
          {hasVoucher && (
            <label className="team-checkbox">
              <input
                type="checkbox"
                checked={voucherTermsAccepted}
                onChange={(e) => setVoucherTermsAccepted(e.target.checked)}
              />
              {t("manual.voucherTerms")}
            </label>
          )}
        </div>
      )}

      {option && (
        <div className="booking-step">
          <div className="step-head">
            <h2>{t("manual.detailsTitle")}</h2>
            <span className="booking-step-number">06</span>
          </div>
          <div className="address-grid">
            {!isFreeConsultation && (
              <div className="full">
                <label htmlFor="mb-payment">{t("manual.payment")}</label>
                <select
                  id="mb-payment"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <option value="stripe">{t("manual.paymentStripe")}</option>
                  <option value="on-site">{t("manual.paymentOnSite")}</option>
                  <option value="later">{t("manual.paymentLater")}</option>
                </select>
                {paymentMethod === "stripe" && (
                  <p className="field-note">{t("manual.paymentStripeHint")}</p>
                )}
              </div>
            )}
            <div className="full">
              <label htmlFor="mb-notes">{t("manual.notes")}</label>
              <textarea
                id="mb-notes"
                rows={3}
                maxLength={500}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("manual.notesPlaceholder")}
              />
            </div>
            <div className="full">
              <label className="team-checkbox">
                <input
                  type="checkbox"
                  checked={notifyCustomer}
                  onChange={(e) => setNotifyCustomer(e.target.checked)}
                />
                {t("manual.notifyCustomer")}
              </label>
            </div>
          </div>
        </div>
      )}

      <div className="team-manual-form-actions">
        <button type="submit" className="auth-submit" disabled={submitting}>
          {submitting ? t("manual.submitting") : t(submitLabelKey)}
        </button>
      </div>
    </form>
  );
};

export default ManualBookingForm;
